import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Alert, Spinner, Row, Col, Accordion } from 'react-bootstrap';
import axios from '../utils/api';
import RichTextEditor from './RichTextEditor';
import './AdminDashboard.css';

function PortfolioSettingsEditor() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState({});
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({
    home_main_image: '',
    home_about_image: '',
    about_avatar_image: '',
    navbar_logo: '',
    resume_pdf_url: '',
    home_greeting: '',
    home_name: '',
    home_location: '',
    home_title: '',
    home_quote: '',
    home_quote_author: '',
    home_job_roles: '',
    primary_color: '#4e69c3',
    secondary_color: '#5e87f5',
    button_color: '#3d4b91',
    button_hover_color: '#4e69c3',
    quote_color: '#5e87f5',
    tech_icon_border_color: '#4e69c3',
    project_card_shadow_color: '#5e87f5',
    scrollbar_color: '#b279d8',
    scrollbar_hover_color: '#de82eb'
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get('/content/portfolio-settings');
      const data = response.data.data;
      if (data) {
        setFormData({
          home_main_image: data.home_main_image || '',
          home_about_image: data.home_about_image || '',
          about_avatar_image: data.about_avatar_image || '',
          navbar_logo: data.navbar_logo || '',
          resume_pdf_url: data.resume_pdf_url || '',
          home_greeting: data.home_greeting || '',
          home_name: data.home_name || '',
          home_location: data.home_location || '',
          home_title: data.home_title || '',
          home_quote: data.home_quote || '',
          home_quote_author: data.home_quote_author || '',
          home_job_roles: data.home_job_roles || '',
          primary_color: data.primary_color || '#4e69c3',
          secondary_color: data.secondary_color || '#5e87f5',
          button_color: data.button_color || '#3d4b91',
          button_hover_color: data.button_hover_color || '#4e69c3',
          quote_color: data.quote_color || '#5e87f5',
          tech_icon_border_color: data.tech_icon_border_color || '#4e69c3',
          project_card_shadow_color: data.project_card_shadow_color || '#5e87f5',
          scrollbar_color: data.scrollbar_color || '#b279d8',
          scrollbar_hover_color: data.scrollbar_hover_color || '#de82eb'
        });
      }
    } catch (err) {
      setError('Failed to load portfolio settings');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (file, fieldName, isPdf = false) => {
    if (!file) return;

    // Validate file type
    const imageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    const pdfTypes = ['application/pdf'];
    const allowedTypes = isPdf ? pdfTypes : imageTypes;
    
    if (!allowedTypes.includes(file.type)) {
      setError(isPdf ? 'Only PDF files are allowed' : 'Only image files are allowed (JPEG, PNG, GIF, WebP)');
      return;
    }

    // Validate file size (10MB for PDF, 5MB for images)
    const maxSize = isPdf ? 10 * 1024 * 1024 : 5 * 1024 * 1024;
    if (file.size > maxSize) {
      setError(`File size must be less than ${isPdf ? '10MB' : '5MB'}`);
      return;
    }

    setUploading({ ...uploading, [fieldName]: true });
    setError('');
    setSuccess('');

    try {
      // Convert file to base64
      const reader = new FileReader();
      reader.onloadend = async () => {
        try {
          const base64String = reader.result;
          const response = await axios.post('/upload', {
            filename: file.name,
            contentType: file.type,
            file: base64String
          });

          if (response.data.success) {
            // Update the form data with the blob URL
            setFormData({ ...formData, [fieldName]: response.data.url });
            setSuccess(`${fieldName.replace(/_/g, ' ')} uploaded successfully!`);
          } else {
            setError(response.data.error || 'Upload failed');
          }
        } catch (err) {
          setError(err.response?.data?.error || 'Failed to upload image');
        } finally {
          setUploading({ ...uploading, [fieldName]: false });
        }
      };
      reader.onerror = () => {
        setError('Failed to read file');
        setUploading({ ...uploading, [fieldName]: false });
      };
      reader.readAsDataURL(file);
    } catch (err) {
      setError('Failed to upload image');
      setUploading({ ...uploading, [fieldName]: false });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);

    try {
      const response = await axios.put('/content/portfolio-settings', formData);
      if (response.data.success) {
        setSuccess('Portfolio settings updated successfully! Please refresh the page to see changes.');
        fetchSettings();
        // Force a page refresh after 1 second to show updated images
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } else {
        setError(response.data.error || 'Failed to update portfolio settings');
      }
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.details?.join(', ') || 'Failed to update portfolio settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-center"><Spinner animation="border" /> Loading...</div>;
  }

  return (
    <div className="editor-card">
      <div className="editor-header">
        <h3>🖼️ Portfolio Images & Settings</h3>
      </div>
      {error && <Alert variant="danger" className="alert-admin" dismissible onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert variant="success" className="alert-admin" dismissible onClose={() => setSuccess('')}>{success}</Alert>}

      <Form onSubmit={handleSubmit} className="admin-form">
        <Accordion defaultActiveKey={['0', '1', '2', '3']} alwaysOpen>
          {/* Images Block */}
          <Accordion.Item eventKey="0" className="mb-3">
            <Accordion.Header>
              <h4 className="mb-0" style={{ color: '#667eea', fontSize: '1.2rem' }}>🖼️ Portfolio Images</h4>
            </Accordion.Header>
            <Accordion.Body>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>Home Main Image (Hero)</strong></Form.Label>
                    <div className="d-flex gap-2 mb-2">
                      <Form.Control
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) handleFileUpload(file, 'home_main_image');
                        }}
                        disabled={uploading.home_main_image}
                        style={{ flex: 1 }}
                      />
                      {uploading.home_main_image && <Spinner size="sm" />}
                    </div>
                    <Form.Control
                      type="text"
                      value={formData.home_main_image}
                      onChange={(e) => setFormData({ ...formData, home_main_image: e.target.value })}
                      placeholder="/Assets/home-main.png or https://..."
                    />
                    <Form.Text className="text-muted">Main hero image on homepage</Form.Text>
                    {formData.home_main_image && (
                      <div className="mt-2">
                        <img 
                          src={formData.home_main_image} 
                          alt="Preview" 
                          style={{ maxWidth: '200px', maxHeight: '150px', borderRadius: '8px', border: '1px solid #ddd' }}
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      </div>
                    )}
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>Home About Section Image</strong></Form.Label>
                    <div className="d-flex gap-2 mb-2">
                      <Form.Control
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) handleFileUpload(file, 'home_about_image');
                        }}
                        disabled={uploading.home_about_image}
                        style={{ flex: 1 }}
                      />
                      {uploading.home_about_image && <Spinner size="sm" />}
                    </div>
                    <Form.Control
                      type="text"
                      value={formData.home_about_image}
                      onChange={(e) => setFormData({ ...formData, home_about_image: e.target.value })}
                      placeholder="/Assets/about.png or https://..."
                    />
                    <Form.Text className="text-muted">Image in "Know Who I'm" section</Form.Text>
                    {formData.home_about_image && (
                      <div className="mt-2">
                        <img 
                          src={formData.home_about_image} 
                          alt="Preview" 
                          style={{ maxWidth: '200px', maxHeight: '150px', borderRadius: '8px', border: '1px solid #ddd' }}
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      </div>
                    )}
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>About Avatar Image</strong></Form.Label>
                    <div className="d-flex gap-2 mb-2">
                      <Form.Control
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) handleFileUpload(file, 'about_avatar_image');
                        }}
                        disabled={uploading.about_avatar_image}
                        style={{ flex: 1 }}
                      />
                      {uploading.about_avatar_image && <Spinner size="sm" />}
                    </div>
                    <Form.Control
                      type="text"
                      value={formData.about_avatar_image}
                      onChange={(e) => setFormData({ ...formData, about_avatar_image: e.target.value })}
                      placeholder="/Assets/avatar.png or https://..."
                    />
                    <Form.Text className="text-muted">Profile picture in About section</Form.Text>
                    {formData.about_avatar_image && (
                      <div className="mt-2">
                        <img 
                          src={formData.about_avatar_image} 
                          alt="Preview" 
                          style={{ maxWidth: '200px', maxHeight: '150px', borderRadius: '8px', border: '1px solid #ddd' }}
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      </div>
                    )}
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>Navbar Logo</strong></Form.Label>
                    <div className="d-flex gap-2 mb-2">
                      <Form.Control
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) handleFileUpload(file, 'navbar_logo');
                        }}
                        disabled={uploading.navbar_logo}
                        style={{ flex: 1 }}
                      />
                      {uploading.navbar_logo && <Spinner size="sm" />}
                    </div>
                    <Form.Control
                      type="text"
                      value={formData.navbar_logo}
                      onChange={(e) => setFormData({ ...formData, navbar_logo: e.target.value })}
                      placeholder="/Assets/logo.png or https://..."
                    />
                    <Form.Text className="text-muted">Logo in navigation bar</Form.Text>
                    {formData.navbar_logo && (
                      <div className="mt-2">
                        <img 
                          src={formData.navbar_logo} 
                          alt="Preview" 
                          style={{ maxWidth: '150px', maxHeight: '50px', borderRadius: '8px', border: '1px solid #ddd' }}
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      </div>
                    )}
                  </Form.Group>
                </Col>
              </Row>
            </Accordion.Body>
          </Accordion.Item>

          {/* Home Section Text Block */}
          <Accordion.Item eventKey="1" className="mb-3">
            <Accordion.Header>
              <h4 className="mb-0" style={{ color: '#667eea', fontSize: '1.2rem' }}>📝 Home Section Text Content</h4>
            </Accordion.Header>
            <Accordion.Body>
              <Card className="mb-3" style={{ backgroundColor: '#f8f9fa', border: '1px solid #e0e0e0' }}>
                <Card.Body>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>Home Greeting</strong></Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.home_greeting}
                      onChange={(e) => setFormData({ ...formData, home_greeting: e.target.value })}
                      placeholder="Hi folks!"
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>Your Name</strong></Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.home_name}
                      onChange={(e) => setFormData({ ...formData, home_name: e.target.value })}
                      placeholder="Firas Bayoudhi"
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>Location</strong></Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.home_location}
                      onChange={(e) => setFormData({ ...formData, home_location: e.target.value })}
                      placeholder="Tunisia, and Currently resident in Brussels"
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>Your Title</strong></Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.home_title}
                      onChange={(e) => setFormData({ ...formData, home_title: e.target.value })}
                      placeholder="IoT Developer"
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>Job Roles (Typewriter Effect) *</strong></Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.home_job_roles}
                      onChange={(e) => setFormData({ ...formData, home_job_roles: e.target.value })}
                      placeholder="Junior IoT Developer,Backend Developer,AI Enthusiast"
                    />
                    <Form.Text className="text-muted">
                      Enter job roles separated by commas. These will appear as a typing animation under your name. 
                      Example: "Junior IoT Developer,Backend Developer,AI Enthusiast"
                    </Form.Text>
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>Quote</strong></Form.Label>
                    <RichTextEditor
                      value={formData.home_quote}
                      onChange={(value) => setFormData({ ...formData, home_quote: value })}
                      placeholder="Success is often achieved by those who don't know that failure is inevitable."
                      rows={3}
                    />
                    <Form.Text className="text-muted">Use the toolbar above to format your quote (Bold, Italic, etc.).</Form.Text>
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>Quote Author</strong></Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.home_quote_author}
                      onChange={(e) => setFormData({ ...formData, home_quote_author: e.target.value })}
                      placeholder="Coco Chanel, fashion designer"
                    />
                  </Form.Group>
                </Card.Body>
              </Card>
            </Accordion.Body>
          </Accordion.Item>

          {/* Resume Block */}
          <Accordion.Item eventKey="2" className="mb-3">
            <Accordion.Header>
              <h4 className="mb-0" style={{ color: '#667eea', fontSize: '1.2rem' }}>📄 Resume / CV</h4>
            </Accordion.Header>
            <Accordion.Body>
              <Form.Group className="mb-3">
                <Form.Label><strong>Resume PDF</strong></Form.Label>
                <div className="d-flex gap-2 mb-2">
                  <Form.Control
                    type="file"
                    accept=".pdf,application/pdf"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) handleFileUpload(file, 'resume_pdf_url', true);
                    }}
                    disabled={uploading.resume_pdf_url}
                    style={{ flex: 1 }}
                  />
                  {uploading.resume_pdf_url && <Spinner size="sm" />}
                </div>
                <Form.Control
                  type="text"
                  value={formData.resume_pdf_url}
                  onChange={(e) => setFormData({ ...formData, resume_pdf_url: e.target.value })}
                  placeholder="/Assets/Bayoudhi_Firas_CV.pdf or https://..."
                />
                <Form.Text className="text-muted">
                  Upload new PDF or enter path/URL manually. Used in the Resume section.
                </Form.Text>
                {formData.resume_pdf_url && (
                  <div className="mt-2">
                    <a 
                      href={formData.resume_pdf_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="btn btn-sm btn-outline-primary"
                    >
                      📄 View Current Resume
                    </a>
                  </div>
                )}
              </Form.Group>
            </Accordion.Body>
          </Accordion.Item>

          {/* Colors Block */}
          <Accordion.Item eventKey="3" className="mb-3">
            <Accordion.Header>
              <h4 className="mb-0" style={{ color: '#667eea', fontSize: '1.2rem' }}>🎨 Color Theme</h4>
            </Accordion.Header>
            <Accordion.Body>
              <Card className="mb-3" style={{ backgroundColor: '#f8f9fa', border: '1px solid #e0e0e0' }}>
                <Card.Body>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          <strong>Primary Color</strong>
                          <div style={{ 
                            width: '30px', 
                            height: '30px', 
                            backgroundColor: formData.primary_color, 
                            border: '2px solid #ddd', 
                            borderRadius: '4px',
                            display: 'inline-block',
                            marginLeft: '10px',
                            verticalAlign: 'middle'
                          }}></div>
                        </Form.Label>
                        <div className="d-flex gap-2">
                          <Form.Control
                            type="color"
                            value={formData.primary_color}
                            onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                            style={{ width: '80px', height: '40px', cursor: 'pointer' }}
                          />
                          <Form.Control
                            type="text"
                            value={formData.primary_color}
                            onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                            placeholder="#4e69c3"
                          />
                        </div>
                        <Form.Text className="text-muted">Main accent color (highlights, purple class)</Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          <strong>Secondary Color</strong>
                          <div style={{ 
                            width: '30px', 
                            height: '30px', 
                            backgroundColor: formData.secondary_color, 
                            border: '2px solid #ddd', 
                            borderRadius: '4px',
                            display: 'inline-block',
                            marginLeft: '10px',
                            verticalAlign: 'middle'
                          }}></div>
                        </Form.Label>
                        <div className="d-flex gap-2">
                          <Form.Control
                            type="color"
                            value={formData.secondary_color}
                            onChange={(e) => setFormData({ ...formData, secondary_color: e.target.value })}
                            style={{ width: '80px', height: '40px', cursor: 'pointer' }}
                          />
                          <Form.Control
                            type="text"
                            value={formData.secondary_color}
                            onChange={(e) => setFormData({ ...formData, secondary_color: e.target.value })}
                            placeholder="#5e87f5"
                          />
                        </div>
                        <Form.Text className="text-muted">Secondary accent color</Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          <strong>Button Color</strong>
                          <div style={{ 
                            width: '30px', 
                            height: '30px', 
                            backgroundColor: formData.button_color, 
                            border: '2px solid #ddd', 
                            borderRadius: '4px',
                            display: 'inline-block',
                            marginLeft: '10px',
                            verticalAlign: 'middle'
                          }}></div>
                        </Form.Label>
                        <div className="d-flex gap-2">
                          <Form.Control
                            type="color"
                            value={formData.button_color}
                            onChange={(e) => setFormData({ ...formData, button_color: e.target.value })}
                            style={{ width: '80px', height: '40px', cursor: 'pointer' }}
                          />
                          <Form.Control
                            type="text"
                            value={formData.button_color}
                            onChange={(e) => setFormData({ ...formData, button_color: e.target.value })}
                            placeholder="#3d4b91"
                          />
                        </div>
                        <Form.Text className="text-muted">Button background color</Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          <strong>Button Hover Color</strong>
                          <div style={{ 
                            width: '30px', 
                            height: '30px', 
                            backgroundColor: formData.button_hover_color, 
                            border: '2px solid #ddd', 
                            borderRadius: '4px',
                            display: 'inline-block',
                            marginLeft: '10px',
                            verticalAlign: 'middle'
                          }}></div>
                        </Form.Label>
                        <div className="d-flex gap-2">
                          <Form.Control
                            type="color"
                            value={formData.button_hover_color}
                            onChange={(e) => setFormData({ ...formData, button_hover_color: e.target.value })}
                            style={{ width: '80px', height: '40px', cursor: 'pointer' }}
                          />
                          <Form.Control
                            type="text"
                            value={formData.button_hover_color}
                            onChange={(e) => setFormData({ ...formData, button_hover_color: e.target.value })}
                            placeholder="#4e69c3"
                          />
                        </div>
                        <Form.Text className="text-muted">Button hover state color</Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          <strong>Quote Color</strong>
                          <div style={{ 
                            width: '30px', 
                            height: '30px', 
                            backgroundColor: formData.quote_color, 
                            border: '2px solid #ddd', 
                            borderRadius: '4px',
                            display: 'inline-block',
                            marginLeft: '10px',
                            verticalAlign: 'middle'
                          }}></div>
                        </Form.Label>
                        <div className="d-flex gap-2">
                          <Form.Control
                            type="color"
                            value={formData.quote_color}
                            onChange={(e) => setFormData({ ...formData, quote_color: e.target.value })}
                            style={{ width: '80px', height: '40px', cursor: 'pointer' }}
                          />
                          <Form.Control
                            type="text"
                            value={formData.quote_color}
                            onChange={(e) => setFormData({ ...formData, quote_color: e.target.value })}
                            placeholder="#5e87f5"
                          />
                        </div>
                        <Form.Text className="text-muted">Quote and blockquote text color</Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          <strong>Project Card Shadow</strong>
                          <div style={{ 
                            width: '30px', 
                            height: '30px', 
                            backgroundColor: formData.project_card_shadow_color, 
                            border: '2px solid #ddd', 
                            borderRadius: '4px',
                            display: 'inline-block',
                            marginLeft: '10px',
                            verticalAlign: 'middle'
                          }}></div>
                        </Form.Label>
                        <div className="d-flex gap-2">
                          <Form.Control
                            type="color"
                            value={formData.project_card_shadow_color}
                            onChange={(e) => setFormData({ ...formData, project_card_shadow_color: e.target.value })}
                            style={{ width: '80px', height: '40px', cursor: 'pointer' }}
                          />
                          <Form.Control
                            type="text"
                            value={formData.project_card_shadow_color}
                            onChange={(e) => setFormData({ ...formData, project_card_shadow_color: e.target.value })}
                            placeholder="#5e87f5"
                          />
                        </div>
                        <Form.Text className="text-muted">Project card shadow and border color</Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          <strong>Tech Icon Border</strong>
                          <div style={{ 
                            width: '30px', 
                            height: '30px', 
                            backgroundColor: formData.tech_icon_border_color, 
                            border: '2px solid #ddd', 
                            borderRadius: '4px',
                            display: 'inline-block',
                            marginLeft: '10px',
                            verticalAlign: 'middle'
                          }}></div>
                        </Form.Label>
                        <div className="d-flex gap-2">
                          <Form.Control
                            type="color"
                            value={formData.tech_icon_border_color}
                            onChange={(e) => setFormData({ ...formData, tech_icon_border_color: e.target.value })}
                            style={{ width: '80px', height: '40px', cursor: 'pointer' }}
                          />
                          <Form.Control
                            type="text"
                            value={formData.tech_icon_border_color}
                            onChange={(e) => setFormData({ ...formData, tech_icon_border_color: e.target.value })}
                            placeholder="#4e69c3"
                          />
                        </div>
                        <Form.Text className="text-muted">Technology icons border color</Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          <strong>Scrollbar Color</strong>
                          <div style={{ 
                            width: '30px', 
                            height: '30px', 
                            backgroundColor: formData.scrollbar_color, 
                            border: '2px solid #ddd', 
                            borderRadius: '4px',
                            display: 'inline-block',
                            marginLeft: '10px',
                            verticalAlign: 'middle'
                          }}></div>
                        </Form.Label>
                        <div className="d-flex gap-2">
                          <Form.Control
                            type="color"
                            value={formData.scrollbar_color}
                            onChange={(e) => setFormData({ ...formData, scrollbar_color: e.target.value })}
                            style={{ width: '80px', height: '40px', cursor: 'pointer' }}
                          />
                          <Form.Control
                            type="text"
                            value={formData.scrollbar_color}
                            onChange={(e) => setFormData({ ...formData, scrollbar_color: e.target.value })}
                            placeholder="#b279d8"
                          />
                        </div>
                        <Form.Text className="text-muted">Scrollbar thumb color</Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          <strong>Scrollbar Hover Color</strong>
                          <div style={{ 
                            width: '30px', 
                            height: '30px', 
                            backgroundColor: formData.scrollbar_hover_color, 
                            border: '2px solid #ddd', 
                            borderRadius: '4px',
                            display: 'inline-block',
                            marginLeft: '10px',
                            verticalAlign: 'middle'
                          }}></div>
                        </Form.Label>
                        <div className="d-flex gap-2">
                          <Form.Control
                            type="color"
                            value={formData.scrollbar_hover_color}
                            onChange={(e) => setFormData({ ...formData, scrollbar_hover_color: e.target.value })}
                            style={{ width: '80px', height: '40px', cursor: 'pointer' }}
                          />
                          <Form.Control
                            type="text"
                            value={formData.scrollbar_hover_color}
                            onChange={(e) => setFormData({ ...formData, scrollbar_hover_color: e.target.value })}
                            placeholder="#de82eb"
                          />
                        </div>
                        <Form.Text className="text-muted">Scrollbar hover state color</Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>

        <div className="mt-4 d-flex justify-content-end">
          <Button className="btn-primary-admin" type="submit" disabled={saving} style={{ minWidth: '200px' }}>
            {saving ? '💾 Saving...' : '💾 Save All Changes'}
          </Button>
        </div>
      </Form>
    </div>
  );
}

export default PortfolioSettingsEditor;

